import { zodResolver } from "@hookform/resolvers/zod"
import { useCallback, useMemo, useRef, useState } from "react"
import type { UseFormReturn } from "react-hook-form"
import { useForm } from "react-hook-form"

import { type InvoiceFormValues, createInvoiceFormSchema } from "../schemas/invoiceForm"
import type { InvoiceFormDialogMode } from "../types/dialog"
import { toInvoiceCreateInput, toInvoiceFormValues, toInvoiceUpdateInput } from "../utils"

import { useCheckInvoiceDuplicates } from "./useCheckInvoiceDuplicates"
import { useCreateInvoice } from "./useCreateInvoice"
import { useCreateInvoiceComment } from "./useCreateInvoiceComment"
import { useUpdateInvoice } from "./useUpdateInvoice"
import type { VendorOption } from "./useVendorOptions"

import type { UploadRowStatus } from "@/components/upload/MultiFileUpload/types"
import { useEntityLockConflictDialog } from "@/entityLock"
import { MutationLockResultStatus } from "@/entityLock/types"
import { type FieldGroupEnum, type Invoice, ReferenceTableName } from "@/graphql/codegen/graphql"
import { type UploadFileItem, type UploadRowProgress, useMultiFileUpload } from "@/hooks/useMultiFileUpload"

interface UseInvoiceFormProps {
  onClose: () => void
  onSuccess?: () => void
  onRefresh?: () => void
  invoice?: Invoice
  mode: InvoiceFormDialogMode
  onLockConflict?: () => void
}

export interface UseInvoiceFormReturn {
  methods: UseFormReturn<InvoiceFormValues>
  canSubmit: boolean
  isProcessing: boolean
  attachmentStatuses: Record<string, UploadRowStatus>
  attachmentProgress: Record<string, UploadRowProgress>
  isUploadingAttachments: boolean
  isCancelConfirmationOpen: boolean
  isSaveConfirmationOpen: boolean
  isResubmitConfirmationOpen: boolean
  isDuplicateWarningOpen: boolean
  isLockConflictOpen: boolean
  lockConflictMessage: string | null
  duplicates: Invoice[]
  reviewingInvoiceId: string | undefined
  selectedVendorOption: VendorOption | null
  handleSubmit: () => void
  handleSaveConfirm: () => Promise<void>
  handleSaveAndResubmit: () => void
  handleSaveAndResubmitConfirm: () => Promise<void>
  handleCancel: () => void
  handleDuplicateWarningCancel: () => void
  handleDuplicateWarningBypass: () => void
  handleReviewOpen: (invoiceId: string) => void
  handleReviewClose: () => void
  handleLockConflictClose: () => void
  handleLockConflictRefresh: () => void
  setIsCancelConfirmationOpen: (open: boolean) => void
  setIsSaveConfirmationOpen: (open: boolean) => void
  setIsResubmitConfirmationOpen: (open: boolean) => void
  setSelectedVendorOption: (option: VendorOption | null) => void
}

export const useInvoiceForm = ({
  onClose,
  onSuccess,
  onRefresh,
  invoice,
  mode,
  onLockConflict,
}: UseInvoiceFormProps): UseInvoiceFormReturn => {
  const schema = useMemo(() => createInvoiceFormSchema(mode), [mode])

  const methods = useForm<InvoiceFormValues>({
    resolver: zodResolver(schema),
    defaultValues: toInvoiceFormValues(invoice),
    mode: "onChange",
  })

  const {
    formState: { isValid, isDirty, errors, isSubmitting },
    getValues,
    trigger,
  } = methods

  const notifyLockConflictUpstream = useCallback(() => {
    onLockConflict?.()
  }, [onLockConflict])

  const { isLockConflictOpen, lockConflictMessage, openLockConflict, closeLockConflict, handleLockConflictRefresh } =
    useEntityLockConflictDialog({ onRefresh, markLost: notifyLockConflictUpstream })

  const [isCancelConfirmationOpen, setIsCancelConfirmationOpen] = useState(false)
  const [isSaveConfirmationOpen, setIsSaveConfirmationOpen] = useState(false)
  const [isResubmitConfirmationOpen, setIsResubmitConfirmationOpen] = useState(false)
  const [isDuplicateWarningOpen, setIsDuplicateWarningOpen] = useState(false)
  const [duplicates, setDuplicates] = useState<Invoice[]>([])
  const [reviewingInvoiceId, setReviewingInvoiceId] = useState<string | undefined>()
  const [selectedVendorOption, setSelectedVendorOption] = useState<VendorOption | null>(null)
  // Ref (not state) because "Add Invoice Anyway" needs to call handleSaveConfirm synchronously
  // in the same tick — a setState would still read `false` in the closure until React commits.
  const isDuplicateWarningBypassedRef = useRef(false)
  // Tracks which submit path opened the duplicate warning so the bypass handler can
  // resume the right confirmation flow ("save" vs "saveAndResubmit").
  const pendingBypassActionRef = useRef<"save" | "saveAndResubmit">("save")
  // Synchronous re-entry guard for handleSaveConfirm / handleSaveAndResubmitConfirm.
  // `isProcessing` (derived from urql/upload `fetching` flags) updates on the next render —
  // a rapid double-click on "Yes, Save" before that commit would fire two submissions.
  const isSavingRef = useRef(false)

  const { checkInvoiceDuplicates, isCheckingDuplicates } = useCheckInvoiceDuplicates()
  const { createInvoice, isCreating } = useCreateInvoice()
  const { createInvoiceComment, isCreating: isCreatingComment } = useCreateInvoiceComment()
  const { updateInvoice, isUpdating } = useUpdateInvoice()
  const {
    statuses: attachmentStatuses,
    progress: attachmentProgress,
    isUploading: isUploadingAttachments,
    uploadFiles,
  } = useMultiFileUpload({ referenceTableName: ReferenceTableName.Invoice })

  const isProcessing =
    isSubmitting || isCheckingDuplicates || isCreating || isCreatingComment || isUpdating || isUploadingAttachments

  const isCreateMode = mode === "create"
  const canSubmit = isValid && isDirty && !Object.keys(errors).length && !isProcessing

  const uploadInvoiceAttachments = useCallback(
    async (attachments: InvoiceFormValues["attachments"], invoiceId: string): Promise<boolean> => {
      const items: UploadFileItem[] = attachments.map((attachment, index) => ({
        id: String(index),
        file: attachment.file,
        fieldGroup: attachment.documentType as FieldGroupEnum,
      }))

      if (!items.length) return true

      const { failures } = await uploadFiles(items, {
        referenceIds: [invoiceId],
        totalFailureMessage: "Invoice saved, but attachments failed to upload. Try again.",
      })

      return failures.length !== items.length
    },
    [uploadFiles]
  )

  const handleSubmit = useCallback(() => {
    void (async () => {
      const isFormValid = await trigger()
      if (!isFormValid) return

      // Bypass is scoped to a single warning → save round-trip. Reset before every fresh
      // duplicate check so a prior bypass cannot leak into a later submission where no
      // warning is shown (e.g. user bypassed, hit "Go Back", fixed the typo, re-saved).
      isDuplicateWarningBypassedRef.current = false
      pendingBypassActionRef.current = "save"

      const formValues = getValues()
      const result = await checkInvoiceDuplicates({
        vendorCompanyId: formValues.vendorId,
        invoiceNumber: formValues.invoiceNumber,
        invoiceDate: formValues.invoiceDate || undefined,
        totalAmount: formValues.totalAmount!,
        // Exclude the invoice being edited so it doesn't flag itself as a duplicate.
        excludeInvoiceId: invoice?.invoiceId,
      })

      // On `failure` the hook has already surfaced an error toast. We still
      // proceed to the save-confirmation so the user can opt to submit and
      // let the BE-side duplicate enforcement act as the safety net.
      if (result.status === "success" && result.duplicates.length > 0) {
        setDuplicates(result.duplicates)
        setIsDuplicateWarningOpen(true)
        return
      }

      setIsSaveConfirmationOpen(true)
    })()
  }, [trigger, getValues, checkInvoiceDuplicates, invoice?.invoiceId])

  const handleSaveConfirm = useCallback(async () => {
    // Keep `isSaveConfirmationOpen` true until either onClose() unmounts the dialog (success)
    // or a terminal failure resets it back to the form (so the user can retry). Closing it
    // upfront causes the InvoiceFormDialog to briefly re-render between the confirm click and
    // the resolution of the create/update mutation — QA reported this as the form "showing
    // again" after Save.
    if (isSavingRef.current) return
    isSavingRef.current = true
    try {
      const formValues = getValues()

      let invoiceId: string | null = null
      if (isCreateMode) {
        invoiceId = await createInvoice(
          toInvoiceCreateInput({
            formValues,
            duplicateWarningBypassed: isDuplicateWarningBypassedRef.current || undefined,
          })
        )
      } else if (invoice) {
        const updateResult = await updateInvoice(
          toInvoiceUpdateInput({
            formValues,
            invoiceId: invoice.invoiceId,
            duplicateWarningBypassed: isDuplicateWarningBypassedRef.current || undefined,
          })
        )
        if (updateResult.status === MutationLockResultStatus.LockConflict) {
          setIsSaveConfirmationOpen(false)
          openLockConflict(updateResult.message)
          return
        }
        invoiceId = updateResult.status === MutationLockResultStatus.Success ? invoice.invoiceId : null
      }

      if (!invoiceId) {
        setIsSaveConfirmationOpen(false)
        return
      }

      const isAttachmentUploadSuccessful = await uploadInvoiceAttachments(formValues.attachments, invoiceId)
      if (!isAttachmentUploadSuccessful) {
        setIsSaveConfirmationOpen(false)
        return
      }

      if (isCreateMode) {
        const commentValue = formValues.comment?.trim()
        if (commentValue) {
          await createInvoiceComment({ invoiceId, value: commentValue })
        }
      }

      onSuccess?.()
      onClose()
    } finally {
      isSavingRef.current = false
    }
  }, [
    createInvoice,
    createInvoiceComment,
    updateInvoice,
    uploadInvoiceAttachments,
    getValues,
    onClose,
    onSuccess,
    openLockConflict,
    isCreateMode,
    invoice,
  ])

  const handleSaveAndResubmit = useCallback(() => {
    void (async () => {
      const isFormValid = await trigger()
      if (!isFormValid) return

      isDuplicateWarningBypassedRef.current = false
      pendingBypassActionRef.current = "saveAndResubmit"

      const formValues = getValues()
      const result = await checkInvoiceDuplicates({
        vendorCompanyId: formValues.vendorId,
        invoiceNumber: formValues.invoiceNumber,
        invoiceDate: formValues.invoiceDate || undefined,
        totalAmount: formValues.totalAmount!,
        excludeInvoiceId: invoice?.invoiceId,
      })

      if (result.status === "success" && result.duplicates.length > 0) {
        setDuplicates(result.duplicates)
        setIsDuplicateWarningOpen(true)
        return
      }

      setIsResubmitConfirmationOpen(true)
    })()
  }, [trigger, getValues, checkInvoiceDuplicates, invoice?.invoiceId])

  const handleSaveAndResubmitConfirm = useCallback(async () => {
    // Same close-on-terminal pattern as handleSaveConfirm — see comment there for the rationale.
    if (isSavingRef.current) return
    if (!invoice) {
      setIsResubmitConfirmationOpen(false)
      return
    }
    isSavingRef.current = true
    try {
      const formValues = getValues()

      const updateResult = await updateInvoice({
        ...toInvoiceUpdateInput({
          formValues,
          invoiceId: invoice.invoiceId,
          duplicateWarningBypassed: isDuplicateWarningBypassedRef.current || undefined,
        }),
        resubmit: true,
      })
      if (updateResult.status === MutationLockResultStatus.LockConflict) {
        setIsResubmitConfirmationOpen(false)
        openLockConflict(updateResult.message)
        return
      }
      if (updateResult.status !== MutationLockResultStatus.Success) {
        setIsResubmitConfirmationOpen(false)
        return
      }

      const isAttachmentUploadSuccessful = await uploadInvoiceAttachments(formValues.attachments, invoice.invoiceId)
      if (!isAttachmentUploadSuccessful) {
        setIsResubmitConfirmationOpen(false)
        return
      }

      onSuccess?.()
      onClose()
    } finally {
      isSavingRef.current = false
    }
  }, [updateInvoice, uploadInvoiceAttachments, getValues, onClose, onSuccess, openLockConflict, invoice])

  const handleCancel = useCallback(() => {
    if (isDirty) {
      setIsCancelConfirmationOpen(true)
      return
    }
    onClose()
  }, [isDirty, onClose])

  const handleDuplicateWarningCancel = useCallback(() => {
    setIsDuplicateWarningOpen(false)
    setDuplicates([])
    setReviewingInvoiceId(undefined)
  }, [])

  const handleDuplicateWarningBypass = useCallback(() => {
    setIsDuplicateWarningOpen(false)
    setDuplicates([])
    setReviewingInvoiceId(undefined)
    isDuplicateWarningBypassedRef.current = true
    if (pendingBypassActionRef.current === "saveAndResubmit") {
      void handleSaveAndResubmitConfirm()
    } else {
      void handleSaveConfirm()
    }
  }, [handleSaveConfirm, handleSaveAndResubmitConfirm])

  const handleReviewOpen = useCallback((invoiceId: string) => {
    setReviewingInvoiceId(invoiceId)
  }, [])

  const handleReviewClose = useCallback(() => {
    setReviewingInvoiceId(undefined)
  }, [])

  return {
    methods,
    canSubmit,
    isProcessing,
    attachmentStatuses,
    attachmentProgress,
    isUploadingAttachments,
    isCancelConfirmationOpen,
    isSaveConfirmationOpen,
    isResubmitConfirmationOpen,
    isDuplicateWarningOpen,
    isLockConflictOpen,
    lockConflictMessage,
    duplicates,
    reviewingInvoiceId,
    selectedVendorOption,
    handleSubmit,
    handleSaveConfirm,
    handleSaveAndResubmit,
    handleSaveAndResubmitConfirm,
    handleCancel,
    handleDuplicateWarningCancel,
    handleDuplicateWarningBypass,
    handleReviewOpen,
    handleReviewClose,
    handleLockConflictClose: closeLockConflict,
    handleLockConflictRefresh,
    setIsCancelConfirmationOpen,
    setIsSaveConfirmationOpen,
    setIsResubmitConfirmationOpen,
    setSelectedVendorOption,
  }
}
